import React from 'react';
import defaultTo from 'lodash/defaultTo';
import { useQuery } from '@apollo/client';
import Tooltip from '@atlaskit/tooltip';
import Avatar from '@atlaskit/avatar';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { fetchContactDetailsFromContactService } from '@css-xp/contact-resources';
import {
  ContactType,
  FetchContactDetailsFromContactServiceQuery,
  FetchContactDetailsFromContactServiceQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { CustomerContactsAvatarGroup } from '@css-xp/ppc-subscription-table/src/components/customer-contacts-avatar-group';
import { NoData } from '@css-xp/ppc-common';
// @ts-ignore
import UserIcon from '@css-xp/base/assets/img/application/icon-user.svg';
import { getResourceAri } from '@css-xp/contact-resources/src/constant';

export interface QuoteContactProps {
  quoteId: string;
  transactionAccountId: string;
  showPrimaryCustomerContact?: boolean;
  contactType?: ContactType;
}

const getSkeletonLoader = () => {
  return (
    <SkeletonLoaderWrapper>
      <SkeletonLoader width="60%" marginBottom="0" />
    </SkeletonLoaderWrapper>
  );
};

export const QuoteContact = ({
  quoteId,
  transactionAccountId,
  showPrimaryCustomerContact,
  contactType,
}: QuoteContactProps) => {
  const client = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const contactTypeValue = showPrimaryCustomerContact ? contactType : ContactType.QUOTE_CONTACT;
  const { data, loading } = useQuery<
    FetchContactDetailsFromContactServiceQuery,
    FetchContactDetailsFromContactServiceQueryVariables
  >(fetchContactDetailsFromContactService, {
    variables: {
      resourceAri: getResourceAri(transactionAccountId, quoteId, ContactType.QUOTE_CONTACT),
      contactType: [contactTypeValue as ContactType],
      transactionAccountId: transactionAccountId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    client,
    skip: !transactionAccountId || !quoteId,
  });

  const quoteContact = defaultTo(data?.contactsByResource, []);
  const primaryQuoteContact = quoteContact?.find((contact) => contact?.isPrimary);

  const renderContact = () => {
    if (showPrimaryCustomerContact) {
      return primaryQuoteContact ? (
        <CustomerContactsAvatarGroup
          contacts={[primaryQuoteContact] as any}
          avatarSize="medium"
          showContactType={true}
        />
      ) : (
        <Tooltip content="Unassigned (Primary)">
          <Avatar src={UserIcon.toString()} testId="unassigned-primary-contact" size="medium" />
        </Tooltip>
      );
    }
    return quoteContact?.[0]?.email ?? <NoData />;
  };

  return loading ? getSkeletonLoader() : <>{renderContact()}</>;
};
